import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import _ from 'lodash';

import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import { toast } from 'react-semantic-toasts';
import { UPDATE_PROJECT_THANK_YOU_REDIRECT_LINK, UPDATE_PROJECT_DISQUALIFY_REDIRECT_LINK } from './graphql';

import {
  Container,
  Content,
  Text,
  Input,
  Button,
  InputGroup,
  Checkbox,
  CheckboxGroup,
} from './styles';

import { urlSchema } from './schema';

const TOAST_SUCCESS_TIME = 1200;
const TOAST_ERROR_TIME = 1500;

const Redirect = ({ project, refetch }) => {
  const {
    id: projectId,
    thankYouRedirectLink = '',
    disqualifyRedirectUrl = '',
    hasMrId: hasMrIdFromProject,
  } = project || {};

  const [thankYouLink, setThankYouLink] = useState(thankYouRedirectLink || '');
  const [disqualifyLink, setDisqualifyLink] = useState(disqualifyRedirectUrl || '');
  const [thankYouLinkSchemaError, setThankYouLinkSchemaError] = useState(null);
  const [disqualifyLinkSchemaError, setDisqualifyLinkSchemaError] = useState(null);
  const [hasMrId, setHasMrId] = useState(hasMrIdFromProject || false);

  const [
    updateProjectThankYouLink,
    { loading: updateThankYouLinkLoading, error: updateThankYouLinkError },
  ] = useMutation(UPDATE_PROJECT_THANK_YOU_REDIRECT_LINK, {
    onCompleted: refetch,
  });

  const [
    updateProjectDisqualifyLink,
    { loading: updateDisqualifyLinkLoading, error: updateDisqualifyLinkError },
  ] = useMutation(UPDATE_PROJECT_DISQUALIFY_REDIRECT_LINK, {
    onCompleted: refetch,
  });

  const handleChangeThankYouLink = (event, { value: url }) => {
    setThankYouLinkSchemaError(null);
    setThankYouLink(url);
    urlSchema.validate(url)
      .catch((err) => {
        setThankYouLinkSchemaError(err.message);
      });
  };

  const handleChangeDisqualifyLink = (event, { value: url }) => {
    setDisqualifyLinkSchemaError(null);
    setDisqualifyLink(url);
    urlSchema.validate(url)
      .catch((err) => {
        setDisqualifyLinkSchemaError(err.message);
      });
  };

  const handleSaveThankYouLink = async () => {
    if (!_.isEmpty(thankYouLinkSchemaError)) {
      return;
    }

    try {
      await updateProjectThankYouLink({
        variables: {
          id: projectId,
          redirectLink: thankYouLink,
          hasMrId,
        },
      });
      toast({
        type: 'success',
        title: 'Success',
        description: 'Redirect link saved successfully',
        time: TOAST_SUCCESS_TIME,
      });
    } catch (err) {
      captureException('UPDATE_PROJECT_THANK_YOU_LINK_FAILURE', err);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Error while saving the redirect link.',
        time: TOAST_ERROR_TIME,
      });
    }
  };

  const handleSaveDisqualifyLink = async () => {
    if (!_.isEmpty(disqualifyLinkSchemaError)) {
      return;
    }

    try {
      await updateProjectDisqualifyLink({
        variables: {
          id: projectId,
          redirectUrl: disqualifyLink,
          hasMrId,
        },
      });
      toast({
        type: 'success',
        title: 'Success',
        description: 'Redirect link saved successfully',
        time: TOAST_SUCCESS_TIME,
      });
    } catch (err) {
      captureException('UPDATE_PROJECT_DISQUALIFY_LINK_FAILURE', err);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Error while saving the redirect link.',
        time: TOAST_ERROR_TIME,
      });
    }
  };

  const handleIsMrId = (event, { checked }) => {
    setHasMrId(checked);
  };

  return (
    <Container>
      <Content>
        <InputGroup>
          <Text as="label" htmlFor="ty-redirect-link">Thank you page redirect link</Text>
          <Input
            id="ty-redirect-link"
            onChange={handleChangeThankYouLink}
            value={thankYouLink}
          />
          {thankYouLinkSchemaError && (
            <Text.Error>{thankYouLinkSchemaError}</Text.Error>
          )}
        </InputGroup>

        <CheckboxGroup>
          <Text as="label" htmlFor="mr-id">MR ID</Text>
          <Checkbox onChange={handleIsMrId} checked={hasMrId} />
        </CheckboxGroup>

        <Button
          loading={updateThankYouLinkLoading}
          onClick={handleSaveThankYouLink}
        >
          Save
        </Button>
        {updateThankYouLinkError && (
          <ErrorHandler
            error={updateThankYouLinkError}
            defaultMessage="Error while saving the redirect link."
          />
        )}
      </Content>

      <Content>
        <InputGroup>
          <Text as="label" htmlFor="disqualify-redirect-link">Disqualify page redirect link</Text>
          <Input
            id="disqualify-redirect-link"
            onChange={handleChangeDisqualifyLink}
            value={disqualifyLink}
          />
          {disqualifyLinkSchemaError && (
            <Text.Error>{disqualifyLinkSchemaError}</Text.Error>
          )}
        </InputGroup>
        <Button
          loading={updateDisqualifyLinkLoading}
          onClick={handleSaveDisqualifyLink}
        >
          Save
        </Button>
        {updateDisqualifyLinkError && (
          <ErrorHandler
            error={updateDisqualifyLinkError}
            defaultMessage="Error while saving the redirect link."
          />
        )}
      </Content>

    </Container>
  );
};

Redirect.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

export default Redirect;
